// Ortssuche mit Vorschlagsliste (Baustein „Wetter", Gebetszeiten): tippen → nach 300 ms ab 2 Zeichen Orte vom Server
// (Open-Meteo) vorschlagen; Auswahl mit Maus oder Tastatur (↑/↓, Enter, Esc). Der eingegebene Text bleibt als
// Ortsname erhalten, Koordinaten lassen sich daneben weiterhin von Hand eintragen.
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { placeDetail, searchPlaces, type Place } from '../api/weather';
import '../styles/place-search.css';

export const SEARCH_DELAY_MS = 300;
export const MIN_CHARS = 2;

type State = { kind: 'idle' } | { kind: 'loading' } | { kind: 'done'; items: Place[] } | { kind: 'error'; message: string };

export function PlaceSearch({ label, value, disabled, onText, onPick }: {
  label: string;
  value: string;
  disabled?: boolean;
  /** Freier Text (Ortsname zur Anzeige). */
  onText: (text: string) => void;
  /** Ort gewählt: setzt Name und Koordinaten. */
  onPick: (p: Place) => void;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [active, setActive] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);

  // Suche nur nach Eingabe des Benutzers (nicht beim Öffnen einer Folie mit gesetztem Ort)
  useEffect(() => {
    if (query === null) return;
    const q = query.trim();
    if (q.length < MIN_CHARS) {
      setState({ kind: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      setState({ kind: 'loading' });
      searchPlaces(q, i18n.language, ctrl.signal).then((items) => {
        setState({ kind: 'done', items });
        setActive(items.length ? 0 : -1);
      }).catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        const message = e instanceof ApiError && e.code === 'weather_disabled' ? t('placeSearch.disabled')
          : t('placeSearch.error', { msg: e instanceof ApiError ? e.message : String((e as Error)?.message ?? e) });
        setState({ kind: 'error', message });
      });
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [query, i18n.language, t]);

  const items = state.kind === 'done' ? state.items : [];
  const pick = (p: Place) => {
    onPick(p);
    setOpen(false);
    setQuery(null);
    setState({ kind: 'idle' });
  };

  useEffect(() => {
    if (active < 0) return;
    listRef.current?.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault();
      setOpen(true);
      setActive((a) => (e.key === 'ArrowDown' ? (a + 1) % items.length : (a <= 0 ? items.length - 1 : a - 1)));
    } else if (e.key === 'Enter') {
      if (open && active >= 0 && items[active]) {
        e.preventDefault();
        pick(items[active]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  const showList = open && query !== null && query.trim().length >= MIN_CHARS && state.kind !== 'idle';
  const listId = `${id}-list`;
  return (
    <div className="field place-search">
      <label htmlFor={`${id}-input`}>{label}</label>
      <div className="place-search-box">
        <input id={`${id}-input`} type="text" role="combobox" autoComplete="off" spellCheck={false} disabled={disabled}
          placeholder={t('placeSearch.placeholder')} value={value}
          aria-autocomplete="list" aria-expanded={showList} aria-controls={listId}
          aria-activedescendant={showList && active >= 0 && items[active] ? `${id}-opt-${active}` : undefined}
          onChange={(e) => {
            onText(e.target.value);
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKey} />
        {showList && (
          <div className="place-search-pop">
            {state.kind === 'loading' && <div className="place-search-msg" role="status">{t('placeSearch.searching')}</div>}
            {state.kind === 'error' && <div className="place-search-msg err" role="alert">{state.message}</div>}
            {state.kind === 'done' && !items.length && <div className="place-search-msg" role="status">{t('placeSearch.none')}</div>}
            <ul id={listId} role="listbox" aria-label={t('placeSearch.results')} ref={listRef} hidden={!items.length}>
              {items.map((p, i) => (
                <li key={`${p.id}-${i}`} id={`${id}-opt-${i}`} role="option" aria-selected={i === active} className={i === active ? 'active' : undefined}
                  // mousedown statt click: sonst schliesst das blur des Eingabefelds die Liste vor der Auswahl
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(p);
                  }}
                  onMouseEnter={() => setActive(i)}>
                  <span className="name">{p.name}</span>
                  <span className="detail">{placeDetail(p)}{p.countryCode ? ` (${p.countryCode})` : ''}</span>
                </li>
              ))}
            </ul>
            {items.length > 0 && <div className="place-search-attr">{t('placeSearch.attribution')}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
